import { AxiosError } from 'axios';

type ApiError = AxiosError<{ message: string }>;

export function sanitizeError(error: Error, defaultMessage: string): Error {
  if (error instanceof AxiosError || (error as AxiosError)?.isAxiosError) {
    const isNetworkError = !(error as AxiosError)?.response?.status;

    if (isNetworkError) {
      error.message = 'Unable to reach server. Please try later';
      return error;
    }

    const serverErrorMessage = (error as ApiError).response?.data?.message;
    console.log(JSON.stringify((error as AxiosError)?.response?.data));
    error.message = serverErrorMessage || defaultMessage;
    return error;
  }

  console.error('API Error', 'Error during error sanitization', error);
  return error;
}

/**
 * Best user-facing message for a thrown API error: prefer the backend's
 * `error_description` (wherever it is nested in the response body), then the
 * error's own message, then the provided fallback. Use this in pages/toasts so
 * we surface the API reason instead of a generic string.
 */
export function getApiErrorMessage(error: unknown, fallback = 'Something went wrong'): string {
  const err = error as any;
  const body = err?.data ?? err?.response?.data;
  const description =
    body?.data?.data?.error_description ??
    body?.data?.error_description ??
    body?.error_description ??
    err?.response?.data?.error_description;
  if (typeof description === 'string' && description.trim()) return description;

  const message = typeof err?.message === 'string' ? err.message.trim() : '';
  if (message && !message.startsWith('HTTP error!')) return message;

  return fallback;
}

export function getApiErrorCode(error: unknown): string | undefined {
  const blobs: unknown[] = [];
  const walk = (value: unknown, depth = 0) => {
    if (!value || typeof value !== 'object' || depth > 4) return;
    blobs.push(value);
    walk((value as { data?: unknown }).data, depth + 1);
  };
  const err = error as { data?: unknown; response?: { data?: unknown } };
  walk(err?.data);
  walk(err?.response?.data);
  walk(error);
  for (const b of blobs) {
    const row = b as { error?: unknown; code?: unknown };
    const code = row.error ?? row.code;
    if (typeof code === 'string' && code.trim() && !code.startsWith('HTTP')) return code;
  }
  return undefined;
}

export function getApiErrorField<T = unknown>(error: unknown, field: string): T | undefined {
  const blobs: unknown[] = [];
  const walk = (value: unknown, depth = 0) => {
    if (!value || typeof value !== 'object' || depth > 4) return;
    blobs.push(value);
    walk((value as { data?: unknown }).data, depth + 1);
  };
  const err = error as { data?: unknown; response?: { data?: unknown } };
  walk(err?.data);
  walk(err?.response?.data);
  walk(error);
  for (const b of blobs) {
    if (b && typeof b === 'object' && field in b) return (b as Record<string, T>)[field];
  }
  return undefined;
}

/** 409 duplicate_topic names the catalog row that already watches those terms. */
export function getDuplicateTopic(error: unknown): { tkid: number } | null {
  const err = error as { status?: number; statusCode?: number; response?: { status?: number }; data?: unknown };
  const status = err?.status ?? err?.statusCode ?? err?.response?.status;
  const blobs: unknown[] = [];
  const walk = (value: unknown, depth = 0) => {
    if (!value || typeof value !== 'object' || depth > 4) return;
    blobs.push(value);
    walk((value as { data?: unknown }).data, depth + 1);
  };
  walk(err?.data);
  walk((err as { response?: { data?: unknown } }).response?.data);

  for (const b of blobs) {
    const row = b as { error?: string; code?: string; tkid?: number; data?: { tkid?: number } };
    const code = row.error ?? row.code;
    const tkid = Number(row.tkid ?? row.data?.tkid);
    if (code === 'duplicate_topic' && Number.isFinite(tkid) && tkid > 0) return { tkid };
    if (status === 409 && Number.isFinite(tkid) && tkid > 0) return { tkid };
  }
  return null;
}

export const withErrorHandling = <T extends (...args: any[]) => Promise<any>>(
  fn: T,
  defaultMessage: string,
): ((...input: Parameters<T>) => Promise<Awaited<ReturnType<T>>>) => {
  return async (...input: Parameters<T>) => {
    try {
      return await fn(...input);
    } catch (error: any) {
      throw sanitizeError(error, defaultMessage);
    }
  };
};